import React, {useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {Paper, MenuItem} from '@material-ui/core';
import {ArrowDropDown as ArrowDropDownIcon, ArrowDropUp as ArrowDropUpIcon} from '@material-ui/icons';
import {isMobile, INSTRUMENT_MAP} from 'utils';
import moveSvg from '../images/move.svg';
import newWindowSvg from '../images/new-window.svg';
import closeSvg from '../images/close.svg';

export const MoveIcon = React.forwardRef(({isPressing, onMouseDown}, ref) => (
  <img
    ref={ref}
    className={classNames({
      'move-icon': true,
      active: isPressing,
    })}
    onMouseDown={onMouseDown}
    width="24"
    height="24"
    src={`${window.NationalInstruments.bentoBox.svgSpriteUrl}${moveSvg.id}`}
  />
));

MoveIcon.propTypes = {
  isPressing: PropTypes.bool.isRequired,
  onMouseDown: PropTypes.func.isRequired,
};

MoveIcon.displayName = 'MoveIcon';

const InstrumentOptions = React.forwardRef(
  ({name, isPressing, style, onMouseDown, popoutInstrument, closeInstrument, isLargeScreen}, ref) => {
    const [active, setActive] = useState(false);
    const [popoutDisabled, setPopoutDisabled] = useState(false);
    const {instrumentRef, instrumentOptionsRef} = ref;
    const refresherRef = useRef();
    const hasMoreOptions = isLargeScreen && !isMobile();
    const showRefresher = hasMoreOptions && active;

    const handlePopoutDisabledChange = (event) => {
      setPopoutDisabled(event.type === 'popoutDisabled');
    };

    const closeMenu = () => {
      setActive(false);
    };

    const handleInstrumentPopout = () => {
      const instrumentButton = instrumentOptionsRef.current
        .closest('.bentobox-wrapper')
        .querySelector('.toolbar instruments-button');
      if (instrumentButton) {
        const instrumentTab = instrumentButton.shadowRoot.querySelector('#instrument-tab');
        instrumentTab.dispatchEvent(new CustomEvent('sfpPopoutStart', {detail: {instrumentName: name}}));
      }
      const instrument = instrumentRef.current;
      instrument.freezeController();
      const settingsJson = instrument.serialize();
      popoutInstrument(settingsJson);
      closeMenu();
    };

    useEffect(
      () => {
        if (instrumentRef.current) {
          instrumentRef.current.addEventListener('popoutDisabled', handlePopoutDisabledChange);
          instrumentRef.current.addEventListener('popoutEnabled', handlePopoutDisabledChange);
        }
        return () => {
          if (instrumentRef.current) {
            instrumentRef.current.removeEventListener('popoutDisabled', handlePopoutDisabledChange);
            instrumentRef.current.removeEventListener('popoutEnabled', handlePopoutDisabledChange);
          }
        };
      },
      [instrumentRef],
    );

    useEffect(
      () => {
        if (!showRefresher) {
          return;
        }
        // Force re-drawing the options drop-down to prevent a weird issue which would make it looks below some instrument components
        refresherRef.current.style.display = 'block';
        requestAnimationFrame(() => (refresherRef.current.style.display = null));
      },
      [active],
    );

    return (
      <>
        {showRefresher && <div ref={refresherRef} className="instrument-options-refresher" />}
        <div ref={instrumentOptionsRef} className="instrument-options" style={style}>
          {hasMoreOptions ? (
            <>
              {!active ? (
                <>
                  <MoveIcon isPressing={isPressing} onMouseDown={onMouseDown} />
                  <ArrowDropDownIcon onClick={() => setActive(true)} />
                </>
              ) : (
                <>
                  <div className="instrument-options-dropdown-outarea" onClick={closeMenu} />
                  <Paper className="instrument-options-dropdown" onClose={closeMenu}>
                    <MenuItem>
                      <MoveIcon isPressing={isPressing} onMouseDown={onMouseDown} />
                      <ArrowDropUpIcon onClick={closeMenu} />
                    </MenuItem>
                    <MenuItem onClick={handleInstrumentPopout} disabled={popoutDisabled}>
                      <img
                        className="popout-icon"
                        width="24"
                        height="24"
                        src={`${window.NationalInstruments.bentoBox.svgSpriteUrl}${newWindowSvg.id}`}
                      />
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        closeInstrument();
                        closeMenu();
                      }}
                    >
                      <img
                        className="close-icon"
                        width="24"
                        height="24"
                        src={`${window.NationalInstruments.bentoBox.svgSpriteUrl}${closeSvg.id}`}
                      />
                    </MenuItem>
                  </Paper>
                </>
              )}
            </>
          ) : (
            <img
              className="close-icon"
              width="24"
              height="24"
              src={`${window.NationalInstruments.bentoBox.svgSpriteUrl}${closeSvg.id}`}
              onClick={() => {
                closeInstrument();
                closeMenu();
              }}
            />
          )}
        </div>
      </>
    );
  },
);

InstrumentOptions.propTypes = {
  name: PropTypes.string.isRequired,
  isPressing: PropTypes.bool.isRequired,
  style: PropTypes.object.isRequired,
  onMouseDown: PropTypes.func.isRequired,
  popoutInstrument: PropTypes.func.isRequired,
  closeInstrument: PropTypes.func.isRequired,
  isLargeScreen: PropTypes.bool.isRequired,
};

InstrumentOptions.displayName = 'InstrumentOptions';

export default InstrumentOptions;
